// src/components/features/analytics/ExportButton.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  FileDownload as DownloadIcon,
  Description as ExcelIcon,
  InsertDriveFile as CsvIcon,
  PictureAsPdf as PdfIcon,
  Code as JsonIcon,
} from '@mui/icons-material';

// Import notification store
import useNotificationStore from '../../../stores/notificationStore';

/**
 * ExportButton Component
 * Reusable button for exporting analytics data in different formats
 */
const ExportButton = ({
  onExport,
  formats = ['excel', 'csv', 'pdf', 'json'],
  buttonText = 'Export',
  disabled = false,
  variant = 'outlined',
  size = 'small',
  color = 'primary',
  sx = {},
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingFormat, setLoadingFormat] = useState(null);
  const { showError, showSuccess } = useNotificationStore();
  
  // Handle button click
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  // Handle menu close
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  // Handle export selection
  const handleExport = async (format) => {
    try {
      setLoading(true);
      setLoadingFormat(format);
      
      // Call the export function
      await onExport(format);
      
      showSuccess(`Report exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      console.error(`Error exporting as ${format}:`, error);
      showError(`Failed to export as ${format}: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
      setLoadingFormat(null);
      handleClose();
    }
  };
  
  // Format icons and labels
  const formatOptions = {
    excel: {
      icon: <ExcelIcon />,
      label: 'Excel Spreadsheet',
      color: '#217346', // Excel green
    },
    csv: {
      icon: <CsvIcon />,
      label: 'CSV File',
      color: theme.palette.info.main,
    },
    pdf: {
      icon: <PdfIcon />,
      label: 'PDF Document',
      color: '#FF0000', // PDF red
    },
    json: {
      icon: <JsonIcon />,
      label: 'JSON Data',
      color: theme.palette.warning.main,
    },
  };
  
  // Filter available formats
  const availableFormats = formats.filter(format => formatOptions[format]);
  
  return (
    <>
      <Button
        variant={variant}
        size={size}
        color={color}
        disabled={disabled || loading}
        startIcon={loading ? <CircularProgress size={18} /> : <DownloadIcon />}
        onClick={handleClick}
        sx={sx}
      >
        {buttonText}
      </Button>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {availableFormats.map((format) => (
          <MenuItem
            key={format}
            onClick={() => handleExport(format)}
            disabled={loading}
          >
            <ListItemIcon sx={{ color: formatOptions[format].color }}>
              {loadingFormat === format ? 
                <CircularProgress size={20} color="inherit" /> : 
                formatOptions[format].icon
              }
            </ListItemIcon>
            <ListItemText>{formatOptions[format].label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

ExportButton.propTypes = {
  /** Callback function for handling export - receives format parameter */
  onExport: PropTypes.func.isRequired,
  /** Available export formats */
  formats: PropTypes.arrayOf(PropTypes.oneOf(['excel', 'csv', 'pdf', 'json'])),
  /** Button text */
  buttonText: PropTypes.string,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Button variant */
  variant: PropTypes.string,
  /** Button size */
  size: PropTypes.string,
  /** Button color */
  color: PropTypes.string,
  /** Additional styles */
  sx: PropTypes.object,
};

export default ExportButton;